import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import api from "@/lib/api";
import { matchesAllTokens } from "@/lib/search";
import { MaterialCard } from "@/components/public/MaterialCard";
import { MaterialDetailModal } from "@/components/public/MaterialDetailModal";
import { AdSlot } from "@/components/public/AdSlot";
import { SeoHead } from "@/components/public/SeoHead";

const CLASSIFICATIONS = [
  {
    key: "GREEN",
    color: "leaf",
    barCls: "bg-leaf",
    borderCls: "border-leaf",
    selectedRingCls: "ring-leaf/60 border-leaf",
    copy: "Sustainable, organic, or biodegradable materials that meet our standards.",
  },
  {
    key: "YELLOW",
    color: "amber",
    barCls: "bg-amber",
    borderCls: "border-amber",
    selectedRingCls: "ring-amber/60 border-amber",
    copy: "Partially sustainable or process-dependent. Accepted in context.",
  },
  {
    key: "RED",
    color: "clay",
    barCls: "bg-clay",
    borderCls: "border-clay",
    selectedRingCls: "ring-clay/60 border-clay",
    copy: "Synthetic, plastic-based, or misaligned with our criteria. Clothing items are only allowed up to 5% of RED materials.",
  },
];

// Case-insensitive, diacritic-insensitive, punctuation-tolerant search across
// the fields that *define* each material — name, aliases ("also known as"),
// short summary, and full description. We deliberately exclude
// ``biodegradability_notes`` and ``certifications`` from the haystack
// because those frequently mention OTHER materials (e.g. Hemp's note
// "less biodegradable when blended with nylon") which would otherwise
// surface Hemp on a search for "nylon".
function materialMatches(material, query) {
  if (!query) return true;
  const haystack = [
    material.name,
    ...(Array.isArray(material.aliases) ? material.aliases : []),
    material.short_summary,
    material.full_description,
  ]
    .filter(Boolean)
    .join(" ");
  return matchesAllTokens(haystack, query);
}

export default function MaterialsGuidePage() {
  const [openSlug, setOpenSlug] = useState(null);
  const [selected, setSelected] = useState("GREEN");
  const [query, setQuery] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["materials-guide"],
    queryFn: async () => (await api.get("/public/materials")).data,
  });

  // Apply search across all classifications, then derive per-classification counts
  // and the active tab's filtered list.
  const filtered = useMemo(() => {
    const out = {};
    for (const c of CLASSIFICATIONS) {
      const items = (data?.[c.key] || []).filter((m) => materialMatches(m, query));
      out[c.key] = items;
    }
    return out;
  }, [data, query]);

  if (isLoading || !data) return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-muted_text">Loading…</div>;

  const trimmedQuery = query.trim();
  const totalMatches = filtered.GREEN.length + filtered.YELLOW.length + filtered.RED.length;
  // Search mode = show every non-empty classification stacked together.
  // Browse mode = the original behaviour: one classification at a time,
  // chosen via the legend tiles.
  const isSearching = !!trimmedQuery;
  const activeItems = filtered[selected] || [];
  const activeCls = CLASSIFICATIONS.find((c) => c.key === selected) || CLASSIFICATIONS[0];

  // Clicking a legend tile is always treated as "browse this classification" —
  // if a search is active, clear it so we drop back to the normal view.
  const selectClassification = (key) => {
    if (query) setQuery("");
    setSelected(key);
  };

  return (
    <div>
      <SeoHead title="Materials Guide — Sustainably Curated" description="How we classify clothing materials as GREEN (sustainable), YELLOW (context-dependent), or RED (synthetic — only ≤5% allowed, subject to review)." path="/materials-guide" />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8">
        <h1 className="text-[1.75rem] uppercase tracking-[0.25em] text-leaf font-medium mb-3">Materials Guide</h1>
        <p className="text-muted_text max-w-2xl mb-4">
          Three classifications govern what we feature.
        </p>
        <p className="text-muted_text">
          Even if a clothing item is mostly made from materials we classify as GREEN, if any YELLOW or RED material is present, we classify the item as YELLOW or RED. However, when we still decide to feature the item, we clearly disclose why.
          {" "}See also all <Link to="/materials-certifications" className="text-forest underline hover:no-underline" data-testid="materials-intro-certifications-link">Materials Certifications</Link> we recognize for these materials.
        </p>
      </section>

      <div className="border-t border-line" />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <p className="text-xs uppercase tracking-[0.2em] text-forest/70">Tap a classification to view its materials</p>
          <div className="relative w-full sm:w-80" data-testid="materials-search-wrapper">
            <Search className="w-4 h-4 text-muted_text absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search materials…"
              className="w-full pl-9 pr-9 py-2 text-sm bg-white border border-line rounded-full focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/20 placeholder:text-muted_text/80"
              data-testid="materials-search-input"
              aria-label="Search materials by name, also-known-as, description, biodegradability, or certifications"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-muted_text hover:text-ink"
                data-testid="materials-search-clear"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-3 mb-4" role="tablist" data-testid="materials-legend">
          {CLASSIFICATIONS.map((c) => (
            <LegendTile
              key={c.key}
              cls={c}
              count={(filtered[c.key] || []).length}
              selected={!isSearching && selected === c.key}
              onSelect={() => selectClassification(c.key)}
            />
          ))}
        </div>

      {isSearching ? (
        // Search mode — render every classification with hits, stacked.
        <div data-testid="materials-search-results">
          {totalMatches === 0 ? (
            <p className="text-muted_text text-sm italic mt-4" data-testid="materials-search-empty">
              No materials match “{trimmedQuery}”. Try a different keyword or clear the search.
            </p>
          ) : (
            CLASSIFICATIONS.map((c) => {
              const items = filtered[c.key] || [];
              if (items.length === 0) return null;
              return (
                <section
                  key={c.key}
                  className="mb-8 scroll-mt-28"
                  data-testid={`materials-section-${c.key.toLowerCase()}`}
                >
                  <div className={`border-t-2 ${c.borderCls} mt-2 mb-4`} aria-hidden />
                  <div className="flex items-baseline justify-between mb-3">
                    <h2 className="font-serif text-xl">{c.key}</h2>
                    <span className="text-[11px] uppercase tracking-wider text-muted_text">
                      {items.length} {items.length === 1 ? "material" : "materials"}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {items.map((m) => (
                      <MaterialCard key={m.id} material={m} onClick={() => setOpenSlug(m.slug)} />
                    ))}
                  </div>
                </section>
              );
            })
          )}
        </div>
      ) : (
        // Browse mode — single classification at a time, chosen via tiles.
        <section className="mb-6 scroll-mt-28" data-testid={`materials-section-${selected.toLowerCase()}`}>
          <div className={`border-t-2 ${activeCls.borderCls} mt-2 mb-6`} aria-hidden />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {activeItems.map((m) => (
              <MaterialCard key={m.id} material={m} onClick={() => setOpenSlug(m.slug)} />
            ))}
          </div>
        </section>
      )}

      <AdSlot slotKey="materials_mid" />

      <MaterialDetailModal slug={openSlug} onClose={() => setOpenSlug(null)} />
      </section>
    </div>
  );
}

function LegendTile({ cls, count, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      role="tab"
      aria-selected={selected}
      data-testid={`materials-legend-${cls.key.toLowerCase()}`}
      className={`text-left bg-white border-2 rounded-2xl px-4 py-3 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-forest/60 ${
        selected
          ? `${cls.selectedRingCls} shadow-sm`
          : "border-line hover:border-forest/40"
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-8 h-2 ${cls.barCls} rounded-full shrink-0`} />
        <h3 className="font-serif text-xl leading-none">{cls.key}</h3>
        <span className={`text-[11px] uppercase tracking-wider ml-auto ${selected ? "text-forest font-medium" : "text-muted_text"}`}>
          {count} {count === 1 ? "material" : "materials"}
        </span>
      </div>
      <p className="text-xs text-muted_text leading-snug mt-1.5 line-clamp-2">{cls.copy}</p>
    </button>
  );
}
